import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { macroDetailData } from '../../../data/macroData';
import { TrendingUp } from 'lucide-react';

interface TradePanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const TradePanel: React.FC<TradePanelProps> = ({ isMinimized, onMinimize }) => {
  const { trade } = macroDetailData;
  const caImprovement = trade.currentAccount - trade.currentAccountPrev;

  return (
    <Panel title="TRADE & CURRENT ACCOUNT" subtitle="2024" status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col gap-2 h-full">
        {/* Exports, Imports, Balance */}
        <div className="grid grid-cols-3 gap-px bg-terminal-border rounded overflow-hidden">
          <div className="bg-terminal-bg p-1.5 text-center">
            <div className="text-xs text-gray-500 uppercase">Exports</div>
            <div className="text-sm font-bold text-data-green">${trade.exports}B</div>
          </div>
          <div className="bg-terminal-bg p-1.5 text-center">
            <div className="text-xs text-gray-500 uppercase">Imports</div>
            <div className="text-sm font-bold text-white">${trade.imports}B</div>
          </div>
          <div className="bg-terminal-bg p-1.5 text-center">
            <div className="text-xs text-gray-500 uppercase">Balance</div>
            <div className="text-sm font-bold text-data-red">${trade.tradeBalance}B</div>
          </div>
        </div>

        {/* Current Account */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 uppercase">Current Account</div>
              <div className="text-lg font-bold text-data-red">{trade.currentAccount}% GDP</div>
            </div>
            <div className="flex items-center gap-1 text-data-green text-xs">
              <TrendingUp size={10} />
              <span>+{caImprovement.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* Top Exports */}
        <div className="bg-terminal-bg rounded p-2">
          <div className="text-xs text-gray-500 uppercase mb-1.5">Top Exports</div>
          <table className="w-full text-xs">
            <tbody>
              {trade.topExports.map((exp) => (
                <tr key={exp.product} className="border-b border-terminal-border/30">
                  <td className="py-0.5 text-gray-300">{exp.product}</td>
                  <td className="py-0.5 text-right text-data-amber">{exp.share}%</td>
                  <td className="py-0.5 text-right text-gray-500">${exp.value}B</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Top Partners */}
        <div className="flex-1 bg-terminal-bg rounded p-2 min-h-0">
          <div className="text-xs text-gray-500 uppercase mb-1.5">Top Partners</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 border-b border-terminal-border">
                <th className="text-left py-0.5 font-medium"></th>
                <th className="text-right py-0.5 font-medium">EXP</th>
                <th className="text-right py-0.5 font-medium">IMP</th>
              </tr>
            </thead>
            <tbody>
              {trade.topPartners.slice(0, 4).map((p) => (
                <tr key={p.country} className="border-b border-terminal-border/30">
                  <td className="py-0.5 text-gray-300 font-medium">{p.country}</td>
                  <td className="py-0.5 text-right text-data-green">{p.exports}%</td>
                  <td className="py-0.5 text-right text-data-red">{p.imports}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.STAT_UZ} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default TradePanel;
